// MainMenuContent.tsx
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Chart from 'react-apexcharts';
import {fetchWithAuth} from '../../core/utils/fetchWithAuth';

interface KPI {
  label: string;
  value: number | string;
}

interface Socio {
  id: number;
  nombre: string;
  estado: string;
  ultimoPago: string;
}

interface DashboardData {
  visibleWidgets: string[];
  summaryCards: {
    totalSocios: {label: string; value: string};
    sociosNuevos: {label: string; value: string};
    ingresosMensuales: {label: string; value: string};
    pagosPendientes: {label: string; value: string};
  };
  evolucionIngresos: {
    title: string;
    labels: string[];
    data: number[];
  };
  comparativaCobranzas: {
    title: string;
    labels: string[];
    data: number[];
  };
  gestionSocios: {
    title: string;
    rows: Socio[];
  };
  resumenCobranzas: {
    title: string;
    labels: string[];
    data: number[];
  };
  desgloseCuotas: {
    title: string;
    labels: string[];
    data: number[];
  };
}

const DashboardWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  const getToken = (): string | null => localStorage.getItem('X-TOKEN');

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      if (!token) throw new Error('No se encontró el token de autenticación');

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'getDashboard',
        }),
      });

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`);
      const data = await response.json();

      if (data.code === 'OK') {
        setDashboardData(data.data);
      } else if (data.code === 'ERROR' && data.returned_message?.includes('JWT expired')) {
        localStorage.removeItem('X-TOKEN');
        navigate('/auth/login');
        return;
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Datos para KPIs desde la API
  const kpiData: KPI[] = dashboardData
    ? [
        {label: dashboardData.summaryCards.totalSocios.label, value: dashboardData.summaryCards.totalSocios.value},
        {label: dashboardData.summaryCards.sociosNuevos.label, value: dashboardData.summaryCards.sociosNuevos.value},
        {label: dashboardData.summaryCards.ingresosMensuales.label, value: dashboardData.summaryCards.ingresosMensuales.value},
        {label: dashboardData.summaryCards.pagosPendientes.label, value: dashboardData.summaryCards.pagosPendientes.value},
      ]
    : [];

  // Datos para la tabla de socios desde la API
  const sociosData: Socio[] = dashboardData?.gestionSocios.rows || [];

  // Configuración para el gráfico de área: Evolución de Ingresos
  const ingresosSeries = [
    {
      name: 'Ingresos',
      data: dashboardData?.evolucionIngresos.data || [],
    },
  ];

  const ingresosOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'area',
      height: 300,
      toolbar: {show: false},
    },
    dataLabels: {enabled: false},
    stroke: {curve: 'smooth'},
    xaxis: {
      categories: dashboardData?.evolucionIngresos.labels || [],
    },
    tooltip: {x: {format: 'MMM'}},
  };

  // Configuración para el gráfico de barras: Comparativa de Cobranzas
  const cobranzasSeries = [
    {
      name: 'Cobranzas',
      data: dashboardData?.comparativaCobranzas.data || [],
    },
  ];

  const cobranzasOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      height: 300,
      toolbar: {show: false},
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
      },
    },
    dataLabels: {enabled: false},
    xaxis: {
      categories: dashboardData?.comparativaCobranzas.labels || [],
    },
    tooltip: {x: {format: 'MMM'}},
  };

  // Configuración para el gráfico donut: Resumen de Cobranzas
  const cobranzasDonutSeries = dashboardData?.resumenCobranzas.data || [];
  const cobranzasDonutOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'donut',
      height: 300,
    },
    labels: dashboardData?.resumenCobranzas.labels || [],
    legend: {
      position: 'bottom',
    },
    dataLabels: {enabled: true},
  };

  // Configuración para el gráfico pie: Servicios y Cursos
  const serviciosPieSeries = dashboardData?.desgloseCuotas.data || [];
  const serviciosPieOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'pie',
      height: 300,
    },
    labels: dashboardData?.desgloseCuotas.labels || [],
    legend: {
      position: 'bottom',
    },
    dataLabels: {enabled: true},
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <div className="text-gray-700 fw-semibold fs-6 mt-4">Cargando dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger d-flex align-items-center p-5">
          <i className="bi bi-exclamation-triangle-fill fs-2x text-danger me-4"></i>
          <div className="d-flex flex-column">
            <h4 className="mb-1 text-danger">Error al cargar el dashboard</h4>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="container-fluid p-4">
        {/* Sección de KPIs */}
        <div className="row mb-4">
          {kpiData.map((kpi, index) => (
            <div className="col-md-3" key={index}>
              <div className="card card-custom">
                <div className="card-body">
                  <h5 className="card-title">{kpi.label}</h5>
                  <h2 className="card-value">{kpi.value}</h2>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sección de Gráficos: Ingresos y Cobranzas */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card card-custom">
              <div className="card-header">
                <h3 className="card-title">Evolución de Ingresos</h3>
              </div>
              <div className="card-body">
                {dashboardData?.evolucionIngresos?.data && dashboardData.evolucionIngresos.data.some((val) => val > 0) ? (
                  <Chart options={ingresosOptions} series={ingresosSeries} type="area" height={300} />
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-graph-up fs-2x text-muted mb-3"></i>
                    <p className="text-muted">No hay datos de ingresos disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card card-custom">
              <div className="card-header">
                <h3 className="card-title">Comparativa de Cobranzas</h3>
              </div>
              <div className="card-body">
                {(dashboardData?.comparativaCobranzas?.data?.length ?? 0) > 0 ? (
                  <Chart options={cobranzasOptions} series={cobranzasSeries} type="bar" height={300} />
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-bar-chart fs-2x text-muted mb-3"></i>
                    <p className="text-muted">No hay datos de cobranzas disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Gestión de Socios */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card card-custom">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-title">Gestión de Socios</h3>
                <button className="btn btn-primary" onClick={() => navigate('/socios/ingresar')}>
                  Nuevo Socio
                </button>
              </div>
              <div className="card-body">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Estado</th>
                      <th>Último Pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sociosData.length > 0 ? (
                      sociosData.map((socio) => (
                        <tr key={socio.id}>
                          <td>{socio.id}</td>
                          <td>{socio.nombre}</td>
                          <td>
                            <span className={`badge badge-light-${socio.estado === 'Activa' ? 'success' : 'danger'}`}>
                              {socio.estado === 'Activa' ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td>{socio.ultimoPago}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No hay datos de socios disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Resumen de Cobranzas y Servicios */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card card-custom">
              <div className="card-header">
                <h3 className="card-title">Resumen de Cobranzas</h3>
              </div>
              <div className="card-body">
                {cobranzasDonutSeries.length > 0 && cobranzasDonutSeries.some((val) => val > 0) ? (
                  <Chart options={cobranzasDonutOptions} series={cobranzasDonutSeries} type="donut" height={300} />
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-pie-chart fs-2x text-muted mb-3"></i>
                    <p className="text-muted">No hay datos de resumen disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card card-custom">
              <div className="card-header">
                <h3 className="card-title">Desglose (Cuota y sobrecuotas)</h3>
              </div>
              <div className="card-body">
                {serviciosPieSeries.length > 0 && serviciosPieSeries.some((val) => val > 0) ? (
                  <Chart options={serviciosPieOptions} series={serviciosPieSeries} type="pie" height={300} />
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-diagram-3 fs-2x text-muted mb-3"></i>
                    <p className="text-muted">No hay datos de cuotas disponibles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      {/*/* Sección de Reportes y Análisis Avanzados
         <div className="row mb-4">
          <div className="col-md-12">
            <div className="card card-custom">
              <div className="card-header">
                <h3 className="card-title">Reportes y Análisis Avanzados</h3>
              </div>
              <div className="card-body">
                <Chart options={reportesOptions} series={reportesSeries} type="line" height={300} />
              </div>
            </div>
          </div>
        </div> */}
      </div>

      {/* <div className="container-fluid p-4">
        <h1>Charts</h1>
        <Charts />
        <h1>Tables</h1>
        <Tables />
        <h1>Feeds</h1>
        <Feeds />
        <h1>Lists</h1>
        <Lists />
        <h1>Mixed</h1>
        <Mixed />
        <h1>Statistics</h1>
        <Statistics />
      </div> */}
    </>
  );
};

export { DashboardWrapper };
